import { create } from 'zustand';
import { createDraftProject } from '@/entities/project';
import {
  confirmDraftProjectFileUpload,
  requestDraftProjectFileUpload,
} from '@/entities/project-file';
import { uploadToS3 } from '@/shared/api/upload-to-s3';
import { sanitizeErrorMessage } from '@/lib/upload-queue/error-message';
import { isUserAbort } from '@/lib/upload-queue/is-user-abort';
import { enqueueUpload } from '@/lib/upload-queue/upload-queue-bindings';
import { logger } from '@/lib/utils/logger';
import { prefillProjectFromDocuments } from '../api/prefillProjectFromDocuments';
import {
  UploadCancelledError,
  type ProjectUploadState,
  type ProjectUploadStore,
  type UploadedFileAsset,
} from './upload.store.types';

const UPLOAD_CONCURRENCY = 3;

const initialState: ProjectUploadState = {
  files: [],
  uploadStatus: 'idle',
  projectId: null,
  error: null,
  uploadedFileAssets: [],
};

/**
 * Performs the 3-step S3 upload chain AND feeds the global upload-queue.
 *
 * Why both stores: the wizard's local `useProjectUploadStore` still drives
 * Next-button gating off `uploadStatus`, while the global queue surfaces the
 * upload in the sidebar tray with real percent + cancel. Both observe the
 * SAME try/catch around the SAME Promise — they cannot diverge for a single
 * file outcome (resolves the "double bookkeeping" risk from proposal #2107).
 *
 * Cross-feature value edge is inverted via the `lib/upload-queue` binding
 * seam — this file never imports from `@/features/upload-queue` directly.
 *
 * Returns the uploaded asset (incl. assetId) so the wizard can hand the
 * documents to the intake-extraction agent for form prefill.
 */
async function uploadSingleFile(projectId: string, file: File): Promise<UploadedFileAsset> {
  const handle = enqueueUpload({ file, source: 'wizard', projectId });

  try {
    const { fileId, versionId, uploadUrl, uploadFields, assetId } =
      await requestDraftProjectFileUpload(projectId, {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        // Uploading with purpose 'artifact' makes the document appear in the
        // project's Library tab.
        purpose: 'artifact',
      });

    await uploadToS3(uploadUrl, uploadFields, file, handle.abortSignal, handle.onProgress);
    await confirmDraftProjectFileUpload(projectId, fileId, versionId);
    handle.markSuccess();

    return { assetId, mimeType: file.type, fileName: file.name, size: file.size };
  } catch (err) {
    // User-cancel race: queue.cancel() already transitioned the item to
    // 'cancelled' and aborted the signal; axios then rejects with a
    // CanceledError. Treat that as the cancel path — do NOT call
    // markFailed (terminal-state guard would no-op anyway, but skipping
    // the call keeps intent clear). Re-throw so the outer try/catch can
    // still set uploadStatus='error' for the wizard UX.
    if (isUserAbort(err)) {
      throw err;
    }
    // Real failure: log the raw error for debugging, then surface a
    // sanitised UI message to the queue so the tray row never leaks
    // axios/S3 implementation detail.
    // eslint-disable-next-line i18next/no-literal-string -- pre-sanitize placeholder; `sanitizeErrorMessage` returns the translated fallback
    const raw = err instanceof Error ? err.message : 'Upload failed';
    logger.error('[create-project] upload failed', { file: file.name, raw, err });
    handle.markFailed(sanitizeErrorMessage(raw));
    throw err;
  }
}

let analyzeController: AbortController | null = null;

export const useProjectUploadStore = create<ProjectUploadStore>((set, get) => ({
  ...initialState,

  addFiles: (newFiles) => {
    set((state) => ({
      files: [...state.files, ...newFiles],
    }));
  },

  removeFile: (index) => {
    set((state) => ({
      files: state.files.filter((_, i) => i !== index),
    }));
  },

  clearFiles: () => {
    set({
      files: [],
    });
  },

  ensureDraftProject: async () => {
    const { projectId } = get();
    if (projectId) return projectId;

    const project = await createDraftProject();
    set({ projectId: project.id });
    return project.id;
  },

  startUploadFlow: async () => {
    const { files, ensureDraftProject } = get();

    try {
      set({ uploadStatus: 'creating-project', error: null, uploadedFileAssets: [] });
      const projectId = await ensureDraftProject();

      set({ uploadStatus: 'uploading' });

      // Aggregate per-file outcomes across all batches via
      // `Promise.allSettled`. With `Promise.all`, the first rejection
      // short-circuited the batch: siblings kept running unobserved and
      // the next batch never started, leaving queue items stuck mid-flight
      // while the wizard already flipped `uploadStatus='error'`.
      //
      // The aggregated failures are then partitioned into user cancels vs
      // real failures. Order of `failures` across concurrent rejected
      // branches is NON-DETERMINISTIC — if user cancels file A and file B
      // real-fails with a 500, `failures[0]` could be either. Treating
      // the first rejection as the surfaced error meant "canceled"
      // sometimes buried the real 500. Worse, an all-cancel scenario
      // re-threw a CanceledError that the wizard toasted as "canceled"
      // and refused to advance — hostile feedback for the user's own
      // intentional action.
      //
      // Decision tree:
      //   - any realFailure → throw the first realFailure (real errors
      //     beat cancel noise in the surfaced message; outer catch sets
      //     uploadStatus='error')
      //   - only cancels, no real failures → throw UploadCancelledError;
      //     uploadStatus stays 'idle', error=null. Wizard catches it
      //     silently and does NOT advance.
      //   - all success → collect the uploaded assets for analysis
      const collected: UploadedFileAsset[] = [];
      const cancels: unknown[] = [];
      const realFailures: unknown[] = [];
      for (let i = 0; i < files.length; i += UPLOAD_CONCURRENCY) {
        const batch = files.slice(i, i + UPLOAD_CONCURRENCY);
        const results = await Promise.allSettled(
          batch.map((file) => uploadSingleFile(projectId, file)),
        );
        for (const result of results) {
          if (result.status === 'fulfilled') {
            collected.push(result.value);
          } else {
            const reason: unknown = result.reason;
            if (isUserAbort(reason)) cancels.push(reason);
            else realFailures.push(reason);
          }
        }
      }

      if (realFailures.length > 0) {
        // Real failure wins. Re-throw the first real failure so the
        // outer try/catch sets uploadStatus='error' with the actionable
        // message. Cancels (if any) stay in the queue as 'cancelled' —
        // those items already transitioned in `uploadSingleFile`.
        throw realFailures[0];
      }

      if (cancels.length > 0) {
        // Pure user cancel — no real failure. The per-file queue items
        // are already 'cancelled'. Throw a sentinel so the wizard can
        // catch silently (no toast, no advance) WITHOUT triggering the
        // outer catch's uploadStatus='error' path.
        throw new UploadCancelledError();
      }

      // Stay on 'uploading' (still part of `isUploading`) instead of flipping to
      // 'done'. The wizard runs analyzeDocuments immediately after, which carries
      // the status uploading → analyzing → done. Passing through 'done' here would
      // hide the "Analyzing documents" overlay for a single frame — a visible blink
      // right as the upload finishes.
      set({ uploadedFileAssets: collected });
      return projectId;
    } catch (error) {
      // Sentinel: pure user-cancel. uploadStatus stays 'idle', no error
      // message — the cancel is not an error condition. Re-throw so the
      // wizard can detect via instanceof and skip its advance step.
      if (error instanceof UploadCancelledError) {
        set({ uploadStatus: 'idle', error: null });
        throw error;
      }
      // eslint-disable-next-line i18next/no-literal-string -- pre-sanitize placeholder; `sanitizeErrorMessage` returns the translated fallback
      const message = error instanceof Error ? error.message : 'Upload failed';
      set({ uploadStatus: 'error', error: message });
      throw error;
    }
  },

  analyzeDocuments: async () => {
    const assets = get().uploadedFileAssets;
    if (assets.length === 0) {
      return { outcome: 'empty' as const };
    }

    const controller = new AbortController();
    analyzeController = controller;
    const timeout = setTimeout(() => controller.abort(), 60_000);

    set({ uploadStatus: 'analyzing' });
    try {
      const prefill = await prefillProjectFromDocuments(assets, controller.signal);
      return { outcome: 'ok' as const, prefill };
    } catch {
      return { outcome: controller.signal.aborted ? ('aborted' as const) : ('error' as const) };
    } finally {
      clearTimeout(timeout);
      analyzeController = null;
    }
  },

  finishUpload: () => {
    set({ uploadStatus: 'done' });
  },

  cancelAnalysis: () => {
    analyzeController?.abort();
  },

  reset: () => {
    set(initialState);
  },
}));
